import { FC, useState, useEffect } from 'react';
import { FAIcon, IconStyle } from '@nextgen-web-framework/all';
import { type IconName } from '@fortawesome/pro-regular-svg-icons';
import type { MediaInner } from '@commerce-product-webcomponents/sdk';
import { ImageComponent, VideoContainer, SourceTypeEnums, ImageType } from '../../Molecules';
import { keyCheck, keyCheckForNav, KeyConstants, useOrientationHook } from '../../../utils';
import type { ProductGalleryProps, IGalleryState } from './types/propsTypes';
import { ProductGalleryStyleType, SwipeEnum } from './types/enums';
import styles from './ProductGallery.module.scss';

const obj = {
  altText: '',
  format: '',
  mediaId: '',
  publicUrl: '',
  url: '',
};

enum MediaType {
  VIDEO = 'video',
  IMAGE = 'image',
}

const ProductGallery: FC<ProductGalleryProps> = (props: ProductGalleryProps): JSX.Element => {
  const { galleryList, openModal, galleryId, sourceKey, productGalleryStyleType } = props;
  const isGalleryEmpty = galleryList?.length === 0;
  const galleryArray = galleryList?.reduce((acc: IGalleryState[], v: MediaInner) => {
    acc.push({
      ...v,
      active: false,
      id: v.type + acc.length,
    });
    return acc;
  }, []);

  const [activeImg, setActiveImg] = useState<IGalleryState>({
    id: '',
    active: false,
    type: '',
    main: { ...obj },
  });
  const [state, setState] = useState<IGalleryState[]>([...galleryArray]);
  const [activeGalleryId, setActiveGalleryId] = useState<string>('');
  const [isPortrait, setIsPortrait] = useState<boolean>(true);

  const hasMoreThanThreeElems = galleryArray?.length > 3;
  const getImageId = (_id: string) => `thumbnail-${_id}`;

  useOrientationHook((e, isChangeEventAdded = false) => {
    if (isChangeEventAdded) {
      setIsPortrait(!!(e && e.matches));
    }
  });

  useEffect(() => {
    changeGalleryImage(galleryArray?.[0], galleryArray?.[0]?.id, false);
  }, [galleryList]);

  useEffect(() => {
    let xDown = 0;
    let yDown = 0;
    const heroGalleryDom = document.getElementById('hero-gallery');
    if (heroGalleryDom) {
      addEventListener('touchstart', handleTouchStart, false);
      addEventListener('touchmove', handleTouchMove, false);
    }

    function handleTouchStart(evt: TouchEvent) {
      xDown = evt.touches[0].clientX;
      yDown = evt.touches[0].clientY;
    }

    function handleTouchMove(evt: TouchEvent) {
      if (!xDown || !yDown) {
        return;
      }

      const xUp = evt.touches[0].clientX;
      const yUp = evt.touches[0].clientY;

      const xDiff = xDown - xUp;
      const yDiff = yDown - yUp;

      if (Math.abs(xDiff) > Math.abs(yDiff) && Math.abs(xDiff) > 12) {
        if (xDiff > 0) {
          swipeImage(SwipeEnum.RIGHT, false);
        } else {
          swipeImage(SwipeEnum.LEFT, false);
        }
      }
      xDown = 0;
      yDown = 0;
    }

    return () => {
      if (heroGalleryDom) {
        removeEventListener('touchstart', handleTouchMove);
        removeEventListener('touchmove', handleTouchMove);
      }
    };
  }, []);

  useEffect(() => {
    if (galleryId) {
      const img = state.find((_img) => _img.id === galleryId);
      if (img) {
        changeGalleryImage(img, galleryId, false);
      }
    }
  }, [galleryId]);

  const changeGalleryImage = (galleryState: IGalleryState, _id: string, isFocus?: boolean) => {
    setActiveImg(galleryState);
    setActiveGalleryId(_id);
    const _state = galleryArray?.map((img: IGalleryState) => {
      const _img = { ...img };
      _img.active = img.id === _id ? true : false;
      return _img;
    });
    setState([..._state]);
    if (isFocus) {
      setTimeout(() => {
        const imageDom = document.getElementById(getImageId(galleryState?.id));
        if (imageDom) {
          imageDom.focus();
        }
      });
    }
  };

  const swipeImage = (swipe: SwipeEnum, isFocus?: boolean) => {
    const activeIndex = state?.findIndex((img: IGalleryState) => img.active === true);
    const nextIndex =
      swipe === SwipeEnum.LEFT
        ? activeIndex - 1 === -1
          ? state.length - 1
          : activeIndex - 1
        : activeIndex + 1 === state.length
        ? 0
        : activeIndex + 1;
    const activeId = state?.find((_img, index) => index === nextIndex)?.id;

    if (activeId) {
      changeGalleryImage(state[nextIndex], activeId, isFocus);
    }
  };

  const checkOpenGallery = () => (e: React.KeyboardEvent) => {
    if (keyCheck(e.code)) {
      openGalleryModal();
    }
  };

  const openGalleryModal = () => {
    if (!isGalleryEmpty) {
      openModal?.(activeGalleryId);
    }
  };

  const handleKeyBoardNavigation = (e: React.KeyboardEvent, direction?: SwipeEnum, isFocus?: boolean) => {
    if (keyCheck(e.code)) {
      swipeImage(direction as SwipeEnum, isFocus);
    } else if (keyCheckForNav(e.code)) {
      swipeImage(KeyConstants.LEFT_NAV === e.code ? SwipeEnum.LEFT : SwipeEnum.RIGHT, isFocus);
    }
  };

  const renderNavigationBtn = (direction: SwipeEnum, key: string, iconName: IconName) => {
    const identifier = `cpw-product-gallery-${key}-control`;
    return (
      hasMoreThanThreeElems && (
        <button
          onClick={() => swipeImage(direction, true)}
          className={styles[identifier]}
          onKeyDown={(e) => handleKeyBoardNavigation(e, direction, true)}
          data-testid={identifier}
        >
          <FAIcon iconName={iconName} iconStyle={IconStyle.REGULAR} />
        </button>
      )
    );
  };

  const getClassName = () => {
    switch (productGalleryStyleType) {
      case ProductGalleryStyleType.PRODUCT_GALLERY_MODAL:
        return 'galleryContainer-modal';
      default:
        return 'galleryContainer';
    }
  };

  const getImageStyleType = () => {
    switch (sourceKey) {
      case SourceTypeEnums.MODAL_GALLERY:
        return {
          main: ImageType.PRODUCT_GALLERY_IMAGE_MODAL,
          thumbnail: ImageType.PRODUCT_GALLERY_IMAGE_MODAL_THUMBNAIL,
        };
      default:
        return {
          main: ImageType.PRODUCT_GALLERY_IMAGE,
          thumbnail: ImageType.PRODUCT_GALLERY_IMAGE_THUMBNAIL,
        };
    }
  };

  const mainLabel = activeImg?.main?.altText || '';
  const thumbnailTray = state?.map((media: IGalleryState) => {
    const { main, active, id, type } = media;
    const { altText = '', publicUrl = '', mediaId = '' } = main || {};
    return (
      <div
        className={`${styles['galleryContainerScrollThumbnail']} ${
          active ? styles['galleryContainerScrollActive'] : ''
        }`}
        onClick={() => changeGalleryImage(media, id, true)}
        onKeyUp={(e) => handleKeyBoardNavigation(e, undefined, true)}
        key={id}
        data-testid={`gallery-elem-${id}-test-id`}
        role="button"
        aria-label={altText}
        tabIndex={0}
        id={getImageId(id)}
      >
        {type?.toLowerCase() === MediaType.IMAGE && (
          <ImageComponent
            link={publicUrl || ''}
            alt={altText}
            imagetype={getImageStyleType().thumbnail}
            data-testid="image-thumbnail-test-id"
          />
        )}
        {type?.toLowerCase() === MediaType.VIDEO && (
          <VideoContainer
            videoId={mediaId || ''}
            isThumbnail={true}
            sourceKey={`${sourceKey}-${SourceTypeEnums.THUMBNAIL}`}
          />
        )}
      </div>
    );
  });

  return (
    <div
      className={`${styles[getClassName()]} ${styles['sticky']} ${
        activeImg?.type === MediaType.VIDEO
          ? styles[`cpw-gallery-container-${isPortrait ? 'portrait' : 'landscape'}`]
          : ''
      }`}
    >
      <div
        id="hero-gallery"
        className={`${styles['hero-gallery']} ${isGalleryEmpty && styles['galleryEmpty']}`}
        data-testid="hero-gallery-test-id"
        role="button"
        aria-label={mainLabel}
        onClick={openGalleryModal}
        onKeyDown={checkOpenGallery}
        tabIndex={0}
      >
        {(activeImg?.type?.toLowerCase() === MediaType.IMAGE || isGalleryEmpty) && (
          <ImageComponent
            link={activeImg?.main?.publicUrl || ''}
            alt={mainLabel}
            imagetype={getImageStyleType().main}
            data-testid="image-main-test-id"
          />
        )}
        {activeImg?.type?.toLowerCase() === MediaType.VIDEO && (
          <VideoContainer
            videoId={activeImg?.main?.mediaId || ''}
            sourceKey={`${sourceKey}-${SourceTypeEnums.MAIN}`}
            className={
              sourceKey === SourceTypeEnums.PRODUCT_DETAIL_GALLERY ? styles['cpw-product-details-video-container'] : ''
            }
            playOnButtonClick={sourceKey === SourceTypeEnums.MODAL_GALLERY}
          />
        )}
      </div>
      <div className={styles['galleryContainerScroll']} data-testid="gallery-test-id">
        {renderNavigationBtn(SwipeEnum.LEFT, 'left', 'chevron-left')}
        <div
          className={styles['galleryContainerInnerScroll']}
          id="galleryContainerInnerScroll"
          data-testid="gallery-container-inner-container"
        >
          {!isGalleryEmpty && thumbnailTray}
        </div>
        {renderNavigationBtn(SwipeEnum.RIGHT, 'right', 'chevron-right')}
      </div>
    </div>
  );
};

export default ProductGallery;
export { ProductGalleryStyleType };
export type { ProductGalleryProps };
